import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { claimPurchase, verifyCheckoutSession } from '../lib/api';
import { getStoredAuthToken } from '../lib/authToken';

export function Bedankt() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') ?? '';
  const { token, user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<'loading' | 'ok' | 'pending' | 'err'>('loading');
  const [detail, setDetail] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [claimOk, setClaimOk] = useState<boolean | null>(null);
  const [claimTried, setClaimTried] = useState(false);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId.startsWith('cs_')) {
      setStatus('err');
      setDetail('Deze bedankt-link is onvolledig. Open de link uit je betaalbevestiging opnieuw, of neem contact op.');
      return;
    }
    verifyCheckoutSession(sessionId)
      .then((r) => {
        if (r.error) {
          setStatus('err');
          setDetail(r.error);
          return;
        }
        setCustomerEmail(r.customer_email ?? null);
        if (r.paid) {
          setPaid(true);
          setStatus('ok');
          setDetail(r.customer_email ? `Bevestigd voor ${r.customer_email}` : null);
        } else {
          setStatus('pending');
          setDetail('Betaling nog niet bevestigd. Vernieuw over een ogenblik.');
        }
      })
      .catch(() => {
        setStatus('err');
        setDetail('Kon de betaling niet controleren.');
      });
  }, [sessionId]);

  useEffect(() => {
    if (!paid || !sessionId.startsWith('cs_') || authLoading) return;
    const t = token ?? getStoredAuthToken();
    if (!t) {
      setClaimOk(false);
      setClaimTried(true);
      return;
    }
    let cancelled = false;
    claimPurchase(t, sessionId).then((c) => {
      if (cancelled) return;
      setClaimTried(true);
      if (c.ok) {
        setClaimOk(true);
      } else {
        setClaimOk(false);
        setDetail(c.message ?? c.error);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [paid, sessionId, token, authLoading]);

  const claimHref = `/registreren?claim_session=${encodeURIComponent(sessionId)}`;
  const claimLogin = `/inloggen?claim_session=${encodeURIComponent(sessionId)}`;

  return (
    <>
      <Seo
        path="/bedankt"
        title="Bedankt voor je bestelling"
        description="Je ComfortTool™-betaling is verwerkt. Koppel je aankoop aan je account met hetzelfde e-mailadres als bij de betaling."
        keywords="ComfortTool bedankt, betaling, aankoop"
        noindex
      />
      <div className="mx-auto max-w-lg px-4 py-16 text-center text-ct-text">
        <h1 className="text-2xl font-bold">Bedankt</h1>
        {status === 'loading' && (
          <p className="mt-4 text-ct-muted" role="status" aria-live="polite">
            Bezig met controleren…
          </p>
        )}
        {status === 'ok' && <p className="mt-4 text-lg text-ct-success">Je betaling is ontvangen.</p>}
        {status === 'pending' && <p className="mt-4 text-ct-warn">{detail}</p>}
        {status === 'err' && (
          <p className="mt-4 text-red-400" role="alert">
            {detail}
          </p>
        )}
        {status === 'ok' && detail && claimOk !== true && (
          <p className="mt-2 text-sm text-ct-muted">{detail}</p>
        )}
        {status === 'ok' && claimTried && claimOk === true && (
          <p className="mt-4 text-sm text-ct-success">
            Je aankoop staat op je account.{' '}
            <Link to="/account" className="font-semibold text-ct-accent hover:underline">
              Naar mijn account
            </Link>
          </p>
        )}
        {status === 'ok' && claimTried && claimOk === false && !user && (
          <div className="mt-6 rounded-xl border border-ct-border bg-ct-surface p-4 text-left text-sm text-ct-muted">
            <p className="font-medium text-ct-text">Koppel je aankoop aan een account</p>
            <p className="mt-2">
              Maak een account met <strong className="text-ct-text">{customerEmail ?? 'het e-mailadres waarmee je betaalde'}</strong>{' '}
              om
              je rapport later opnieuw te bekijken of te downloaden.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Link
                to={claimHref}
                className="inline-flex justify-center rounded-xl bg-ct-accent px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-500"
              >
                Registreren
              </Link>
              <Link
                to={claimLogin}
                className="inline-flex justify-center rounded-xl border border-ct-border px-4 py-2.5 text-sm font-semibold text-ct-text hover:bg-ct-elevated"
              >
                Inloggen
              </Link>
            </div>
          </div>
        )}
        {status === 'ok' && claimTried && claimOk === false && user && (
          <p className="mt-4 text-sm text-ct-warn">
            Dit account komt niet overeen met het betaal-e-mailadres. Gebruik{' '}
            <Link to={claimLogin} className="text-ct-accent hover:underline">
              inloggen
            </Link>{' '}
            met {customerEmail ?? 'het e-mailadres van je betaling'}.
          </p>
        )}
        <p className="mt-10 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Link to="/" className="font-semibold text-ct-accent hover:underline">
            Naar home
          </Link>
          <span className="hidden text-ct-muted sm:inline">·</span>
          <Link to="/scan" className="font-semibold text-ct-accent hover:underline">
            Nieuwe scan
          </Link>
        </p>
      </div>
    </>
  );
}
